import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import {
  ArrowLeft, Plus, X, Loader2, CheckCircle2, AlertCircle, CreditCard, Layers
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Health & Social Care', 'Business & Management', 'Education & Training', 'IT & Technology', 'Law', 'Early Years & Childcare'];
const LEVELS = ['Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7'];
const BODIES = ['OTHM', 'QUALIFI'];

const EMPTY = {
  title: '', slug: '', category: CATEGORIES[0], level: LEVELS[1], duration: '',
  price: '', original_price: '', on_sale: false, description: '', modules: [],
  awarding_body: BODIES[0], image: '', featured: false, enroll_count: 0,
  rating: 5.0, review_count: 0, payment_plans: [], installment_enabled: true, active: true,
  who_is_for: '', entry_requirements: [], assessment: '', progression: '',
};

function installmentPerPayment(price) {
  const p = parseFloat(price) || 0;
  if (p <= 0) return null;
  return Math.round((p + 100) / 2);
}

export default function AdminCourseForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { authHeaders } = useAdminAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [newModule, setNewModule] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const categoryOptions = [...new Set([...CATEGORIES, form.category].filter(Boolean))];

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/admin/courses`, { headers: authHeaders })
      .then(res => {
        const course = res.data.find(c => c.id === id);
        if (course) {
          setForm({
            ...course,
            modules: Array.isArray(course.modules) ? course.modules : [],
            payment_plans: Array.isArray(course.payment_plans) ? course.payment_plans : [],
            installment_enabled: course.installment_enabled !== false,
            who_is_for: course.who_is_for || '',
            entry_requirements: Array.isArray(course.entry_requirements) ? course.entry_requirements : [],
            assessment: course.assessment || '',
            progression: course.progression || '',
          });
        }
      })
      .finally(() => setLoading(false));
  }, [authHeaders, id, isEdit]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const addModule = () => {
    if (!newModule.trim()) return;
    setForm(prev => ({ ...prev, modules: [...prev.modules, newModule.trim()] }));
    setNewModule('');
  };

  const removeModule = (i) => setForm(prev => ({ ...prev, modules: prev.modules.filter((_, idx) => idx !== i) }));

  const addRequirement = () => {
    if (!newRequirement.trim()) return;
    setForm(prev => ({ ...prev, entry_requirements: [...prev.entry_requirements, newRequirement.trim()] }));
    setNewRequirement('');
  };

  const removeRequirement = (i) =>
    setForm(prev => ({ ...prev, entry_requirements: prev.entry_requirements.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        original_price: parseFloat(form.original_price) || null,
        enroll_count: parseInt(form.enroll_count) || 0,
        rating: parseFloat(form.rating) || 5.0,
        review_count: parseInt(form.review_count) || 0,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/courses/${id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/admin/courses`, payload, { headers: authHeaders });
      }
      setSuccess(true);
      setTimeout(() => navigate('/admin/courses'), 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed. Check all required fields.');
    } finally {
      setSaving(false);
    }
  };

  const perInstallment = installmentPerPayment(form.price);
  const installmentTotal = perInstallment ? perInstallment * 2 : null;

  if (loading) return <AdminLayout><div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-blue-600" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-3xl space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/courses')} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Course' : 'New Course'}</h1>
            <p className="text-gray-500 text-sm">{isEdit ? 'Update course details' : 'Add a new course to the catalogue'}</p>
          </div>
        </div>

        {success && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">
            <CheckCircle2 size={15} /> {isEdit ? 'Course updated' : 'Course created'} successfully! Redirecting…
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Basic info */}
          <Section title="Basic Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Course Title *">
                  <input required value={form.title} onChange={set('title')} placeholder="e.g. Level 3 Diploma in Business Management"
                    className={inputCls} />
                </Field>
              </div>
              <Field label="URL Slug">
                <input value={form.slug} onChange={set('slug')} placeholder="auto-generated if left empty"
                  className={inputCls} />
              </Field>
              <Field label="Awarding Body">
                <select value={form.awarding_body} onChange={set('awarding_body')} className={inputCls}>
                  {BODIES.map(b => <option key={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <>
                  <input
                    list="course-categories"
                    value={form.category}
                    onChange={set('category')}
                    placeholder="Select or type a new category"
                    className={inputCls}
                  />
                  <datalist id="course-categories">
                    {categoryOptions.map(category => <option key={category} value={category} />)}
                  </datalist>
                </>
              </Field>
              <Field label="Level">
                <select value={form.level} onChange={set('level')} className={inputCls}>
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Duration">
                <input value={form.duration} onChange={set('duration')} placeholder="e.g. 6-9 months" className={inputCls} />
              </Field>
            </div>
            <Field label="Description">
              <textarea rows={4} value={form.description} onChange={set('description')}
                placeholder="Describe the course…"
                className={inputCls + ' resize-none'} />
            </Field>
          </Section>

          {/* Pricing */}
          <Section title="Pricing">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Full Price (£) *">
                <input required type="number" min="0" step="0.01" value={form.price} onChange={set('price')} className={inputCls} />
              </Field>
              <Field label="Original / RRP (£)">
                <input type="number" min="0" step="0.01" value={form.original_price || ''} onChange={set('original_price')} className={inputCls} />
              </Field>
              <Field label="On Sale">
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={form.on_sale} onChange={set('on_sale')} className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700">Mark as sale</span>
                </label>
              </Field>
            </div>

            {/* Installment toggle */}
            <div className="rounded-xl border border-gray-200 overflow-hidden mt-1">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Layers size={15} className="text-blue-600" />
                  <span className="text-sm font-medium text-gray-800">2-Instalment Option</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.installment_enabled}
                    onChange={set('installment_enabled')}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-xs text-gray-600">Enable for this course</span>
                </label>
              </div>

              <div className="px-4 py-3">
                {form.installment_enabled && perInstallment ? (
                  <div className="grid grid-cols-2 gap-3">
                    <PriceCard
                      icon={<CreditCard size={14} className="text-blue-600" />}
                      label="Pay in Full"
                      main={`£${parseFloat(form.price).toLocaleString('en-GB')}`}
                      sub="Single payment"
                      highlight
                    />
                    <PriceCard
                      icon={<Layers size={14} className="text-purple-600" />}
                      label="2 Instalments"
                      main={`£${perInstallment.toLocaleString('en-GB')} × 2`}
                      sub={`£${installmentTotal?.toLocaleString('en-GB')} total (+£100)`}
                    />
                  </div>
                ) : form.installment_enabled ? (
                  <p className="text-xs text-gray-400">Enter a price above to preview instalment amounts.</p>
                ) : (
                  <p className="text-xs text-gray-400">Students will only see the full-price option.</p>
                )}
              </div>
            </div>
          </Section>

          {/* Course modules */}
          <Section title="Course Modules">
            <div className="space-y-2">
              {form.modules.map((mod, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{mod}</span>
                  <button type="button" onClick={() => removeModule(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={newModule} onChange={e => setNewModule(e.target.value)}
                  placeholder="Add a module name…"
                  className={inputCls + ' flex-1'}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addModule())} />
                <button type="button" onClick={addModule} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </Section>

          {/* Course details (shown as sections on the course page) */}
          <Section title="Course Details">
            <Field label="Who Is This Course For?">
              <textarea rows={3} value={form.who_is_for} onChange={set('who_is_for')}
                placeholder="Describe the ideal learner for this course…"
                className={inputCls + ' resize-none'} />
            </Field>
            <Field label="Entry Requirements">
              <div className="space-y-2">
                {form.entry_requirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{req}</span>
                    <button type="button" onClick={() => removeRequirement(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input value={newRequirement} onChange={e => setNewRequirement(e.target.value)}
                    placeholder="Add an entry requirement…"
                    className={inputCls + ' flex-1'}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRequirement())} />
                  <button type="button" onClick={addRequirement} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </Field>
            <Field label="How You Will Be Assessed">
              <textarea rows={3} value={form.assessment} onChange={set('assessment')}
                placeholder="e.g. Written assignments marked by your tutor — no exams…"
                className={inputCls + ' resize-none'} />
            </Field>
            <Field label="Career & University Progression">
              <textarea rows={3} value={form.progression} onChange={set('progression')}
                placeholder="What can learners do after completing this course?"
                className={inputCls + ' resize-none'} />
            </Field>
          </Section>

          {/* Media & Stats */}
          <Section title="Image & Stats">
            <Field label="Image URL">
              <input value={form.image} onChange={set('image')} placeholder="https://images.unsplash.com/…" className={inputCls} />
              {form.image && (
                <img src={form.image} alt="preview" className="mt-2 h-28 w-full object-cover rounded-lg" onError={e => e.target.style.display = 'none'} />
              )}
            </Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Enrol Count">
                <input type="number" min="0" value={form.enroll_count} onChange={set('enroll_count')} className={inputCls} />
              </Field>
              <Field label="Rating (0-5)">
                <input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={set('rating')} className={inputCls} />
              </Field>
              <Field label="Review Count">
                <input type="number" min="0" value={form.review_count} onChange={set('review_count')} className={inputCls} />
              </Field>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={set('featured')} className="w-4 h-4" />
                <span className="text-sm text-gray-700">Featured on homepage</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={set('active')} className="w-4 h-4" />
                <span className="text-sm text-gray-700">Active (visible to public)</span>
              </label>
            </div>
          </Section>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving || success}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors">
              {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={15} /> {isEdit ? 'Update Course' : 'Create Course'}</>}
            </button>
            <button type="button" onClick={() => navigate('/admin/courses')}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

function PriceCard({ icon, label, main, sub, highlight }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <p className="text-base font-bold text-gray-900">{main}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-900';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h2 className="font-semibold text-gray-900 text-sm border-b border-gray-100 pb-2">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
